import { FC, useMemo } from "react";
import styled from "styled-components";

export type HighlightRange = {
  start: number;
  end: number;
  type?: string;
};

const Mark = styled.span`
  background: var(--x-yellow, #f2c94c);
  color: var(--x-gray-900, #1a1726);
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 600;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

const mergeRanges = (ranges: HighlightRange[]): HighlightRange[] => {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: HighlightRange[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = merged[merged.length - 1];
    const curr = sorted[i];
    if (curr.start <= prev.end) {
      prev.end = Math.max(prev.end, curr.end);
      if (curr.type && curr.type !== prev.type) {
        prev.type = prev.type ? `${prev.type}, ${curr.type}` : curr.type;
      }
    } else {
      merged.push({ ...curr });
    }
  }
  return merged;
};

type Props = {
  text: string;
  highlights: HighlightRange[];
};

const HighlightedText: FC<Props> = ({ text, highlights }) => {
  const segments = useMemo(() => {
    if (!text) return [];
    const valid = highlights
      .filter((h) => h.end > h.start && h.start < text.length && h.end > 0)
      .map((h) => ({
        start: Math.max(0, h.start),
        end: Math.min(text.length, h.end),
        type: h.type,
      }));
    const merged = mergeRanges(valid);
    const result: Array<
    | { kind: "text"; value: string; key: string }
    | { kind: "mark"; value: string; key: string; type?: string }
    > = [];
    let cursor = 0;
    merged.forEach((h, i) => {
      if (h.start > cursor) {
        result.push({ kind: "text", value: text.slice(cursor, h.start), key: `t-${i}` });
      }
      result.push({
        kind: "mark", value: text.slice(h.start, h.end), key: `h-${i}`, type: h.type,
      });
      cursor = h.end;
    });
    if (cursor < text.length) {
      result.push({ kind: "text", value: text.slice(cursor), key: "t-end" });
    }
    return result;
  }, [text, highlights]);

  if (segments.length === 0) return <span>{text}</span>;
  return (
    <>
      {segments.map((seg) => (
        seg.kind === "mark"
          ? <Mark key={seg.key} title={seg.type}>{seg.value}</Mark>
          : <span key={seg.key}>{seg.value}</span>
      ))}
    </>
  );
};

export default HighlightedText;
